import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RotateCw } from "lucide-react";

const RESULT = (s) => (s === "listed" || s === "imported" ? "accepted" : s === "duplicate" ? "duplicate" : "rejected");
const BADGE = { accepted: "bg-emerald-100 text-emerald-800", duplicate: "bg-amber-100 text-amber-800", rejected: "bg-red-100 text-red-800" };
const FILTERS = ["all", "accepted", "duplicate", "rejected"];
const WHERE = { inventory: "Inventory URL", test: "Admin test", admin_resend: "Admin resend" };

const resend = async (eventId, onChanged, setBusy) => {
  setBusy(eventId || "all");
  try {
    const { data } = await api.post("/admin/marketplace/resend", { event_id: eventId || "" });
    if (!data.results.length) toast.info("No failed leads with saved data to resend");
    else if (data.failed) toast.warning(`${data.resent} resent, ${data.failed} still failing: ${data.results.find((r) => !r.ok).detail}`);
    else toast.success(`${data.resent} lead${data.resent === 1 ? "" : "s"} resent`);
    onChanged?.();
  } catch (e) { toast.error(apiError(e)); }
  setBusy("");
};

const Row = ({ e, busy, onResend }) => (
  <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-100 py-2 text-sm last:border-0" data-testid={`delivery-row-${e.id}`}>
    <div className="min-w-0">
      <Badge className={`mr-2 border-0 capitalize ${BADGE[RESULT(e.status)]}`}>{RESULT(e.status)}</Badge>
      lead <b>{e.astoria_lead_id || "—"}</b> · {e.mlo_email || "unknown sender"}
      <div className="break-words text-xs text-slate-500">{e.detail || e.status.replace(/_/g, " ")}</div>
      {e.resent_at && <div className={`text-xs ${e.resent_ok ? "text-emerald-700" : "text-red-700"}`} data-testid={`delivery-resent-${e.id}`}>
        Resent {new Date(e.resent_at).toLocaleString()}: {e.resent_detail}</div>}
    </div>
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-500">{WHERE[e.endpoint] || "Astoria URL"} · {new Date(e.created_at).toLocaleString()}</span>
      {e.has_payload && !e.resent_ok && <Button size="sm" variant="outline" className="h-7" disabled={!!busy} onClick={() => onResend(e.id)} data-testid={`delivery-resend-${e.id}`}>
        <RotateCw className={`mr-1 h-3.5 w-3.5 ${busy === e.id ? "animate-spin" : ""}`} />Resend</Button>}
    </div>
  </div>
);

export const DeliveryLog = ({ events, held = 0, onChanged }) => {
  const [f, setF] = useState("all");
  const [busy, setBusy] = useState("");
  const rows = events.filter((e) => f === "all" || RESULT(e.status) === f);
  const failed = events.filter((e) => e.has_payload && !e.resent_ok).length + held;
  const onResend = (id) => resend(id, onChanged, setBusy);
  return (
    <Card className="p-6" data-testid="admin-astoria-events">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-slate-900">Webhook activity log</h3>
        <div className="flex flex-wrap items-center gap-1">{FILTERS.map((k) => (
          <button key={k} onClick={() => setF(k)} data-testid={`delivery-filter-${k}`}
            className={`rounded-full px-3 py-1 text-xs capitalize transition-colors ${f === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {k} ({k === "all" ? events.length : events.filter((e) => RESULT(e.status) === k).length})
          </button>))}
          <Button size="sm" className="ml-1 h-7 bg-slate-900 hover:bg-slate-800" disabled={!failed || !!busy} onClick={() => onResend("")} data-testid="delivery-resend-all">
            <RotateCw className={`mr-1 h-3.5 w-3.5 ${busy === "all" ? "animate-spin" : ""}`} />Resend failed leads ({failed})</Button>
        </div>
      </div>
      <p className="mb-2 text-xs text-slate-500">Failed deliveries received from now on keep their lead data so you can resend them here. Older failures must be replayed from Zapier's Zap History.{held > 0 && ` Includes ${held} earlier lead${held === 1 ? "" : "s"} held for review.`}</p>
      {rows.length === 0 ? <p className="text-sm text-slate-400">No delivery attempts yet.</p> : rows.map((e) => <Row key={e.id} e={e} busy={busy} onResend={onResend} />)}
    </Card>
  );
};
